import { isProblemWithCode, toUiError } from "../errors/uiError";
import { enumMembers, type ApiSchema } from "./apiSchema";
import { jsonBody, requestJson, requestWithoutContent } from "./http/httpClient";
import { readBoolean, readLiteral, readObject, readOneOf, readString, type JsonObject } from "./http/jsonDecoding";
import { readAccessDecisions, type AccessDecisions } from "./access/decisions";

/**
 * Accounts (module A, FR-ACCESS-1; `docs/components/accounts.md`): first-administrator setup,
 * invitations, self-registration with email verification and password reset (Q-36), and the
 * browser's cookie session. Routes: `server/src/dmw/api/routes/accounts/`.
 */

export type AccountRole = ApiSchema<"AccountRole">;

export interface SignedInAccount {
  readonly userId: string;
  readonly email: string;
  readonly displayName: string;
  readonly accountRole: AccountRole;
}

/** A cookie session as the server reports it. */
export interface BrowserSession {
  readonly account: SignedInAccount;
  /** Sent as `X-CSRF-Token` on every unsafe request of this session. */
  readonly csrfToken: string;
  /** ISO 8601. The session ends then; there is no refresh. */
  readonly expiresAt: string;
  /** Each installation action, such as `create_project`: allowed, or refused with why (SOT-02). */
  readonly decisions: AccessDecisions;
}

export interface SignInCredentials {
  readonly email: string;
  readonly password: string;
}

export interface FirstAdministratorSetup {
  readonly setupToken: string;
  readonly email: string;
  readonly displayName: string;
  readonly password: string;
}

export interface InvitationAcceptance {
  readonly acceptanceToken: string;
  readonly displayName: string;
  readonly password: string;
}

export const ACCOUNT_ROLES = enumMembers<AccountRole>({ administrator: true, member: true });

/** Whether this installation still waits for its first administrator. */
export function readFirstAdministratorSetupOpen(signal?: AbortSignal): Promise<boolean> {
  return requestJson({
    method: "GET",
    path: "/accounts/first-administrator",
    signal,
    decodeResponse: (body) => readBoolean(readObject(body, "first-administrator status"), "setup_open"),
  });
}

/** Create the first administrator with the setup token from the server's configuration. */
export function setUpFirstAdministrator(setup: FirstAdministratorSetup): Promise<SignedInAccount> {
  const body = {
    setup_token: setup.setupToken,
    email: setup.email,
    display_name: setup.displayName,
    password: setup.password,
  } satisfies ApiSchema<"FirstAdministratorSetupBody">;
  return requestJson({
    method: "POST",
    path: "/accounts/first-administrator",
    body: jsonBody(body),
    decodeResponse: (response) => decodeUser(readObject(response, "user")),
  });
}

/** What an account that isn't active yet waits for, as the server says (F133). */
export type AccountWait = ApiSchema<"AccountWait">;

export const ACCOUNT_WAITS = enumMembers<AccountWait>({ confirm_email: true, confirm_email_then_approval: true, approval: true });

/** Accept an invitation: creates the account (and its project membership), and says what it
 * waits for; `null`: it is active, its owner signs in. */
export function acceptInvitation(acceptance: InvitationAcceptance): Promise<AccountWait | null> {
  const body = {
    acceptance_token: acceptance.acceptanceToken,
    display_name: acceptance.displayName,
    password: acceptance.password,
  } satisfies ApiSchema<"InvitationAcceptanceBody">;
  return requestJson({
    method: "POST",
    path: "/accounts/invitations/acceptance",
    body: jsonBody(body),
    decodeResponse: (response) => {
      const accepted = readObject(response, "accepted invitation");
      return accepted["waits_for"] === null ? null : readOneOf(accepted, "waits_for", ACCOUNT_WAITS);
    },
  });
}

/** Log in. The server sets the HttpOnly session cookie; the CSRF token comes back here. */
export function startSession(credentials: SignInCredentials): Promise<BrowserSession> {
  const body = {
    email: credentials.email,
    password: credentials.password,
    transport: "cookie",
  } satisfies ApiSchema<"LoginBody">;
  return requestJson({
    method: "POST",
    path: "/accounts/sessions",
    body: jsonBody(body),
    decodeResponse: (response) =>
      decodeBrowserSession(readObject(readObject(response, "started session")["session"], "session")),
  });
}

/** The session of this browser, or `null` when it has none (or it has ended). */
export async function readCurrentSession(signal?: AbortSignal): Promise<BrowserSession | null> {
  try {
    return await requestJson({
      method: "GET",
      path: "/accounts/sessions/current",
      signal,
      decodeResponse: (response) => decodeBrowserSession(readObject(response, "session")),
    });
  } catch (caught) {
    if (isProblemWithCode(toUiError(caught), "authentication_required")) {
      return null;
    }
    throw caught;
  }
}

/** Log out. A session that has ended already counts as logged out. */
export async function endCurrentSession(): Promise<void> {
  try {
    await requestWithoutContent({ method: "DELETE", path: "/accounts/sessions/current" });
  } catch (caught) {
    if (!isProblemWithCode(toUiError(caught), "authentication_required")) {
      throw caught;
    }
  }
}

/** What the sign-in page offers (Q-36): self-registration, and whether the server sends mail. */
export interface RegistrationStatus {
  readonly selfRegistration: SelfRegistration;
  /** False when the server sends no mail: the operator must configure email for verification. */
  readonly emailDelivery: boolean;
}

export type SelfRegistration = ApiSchema<"SelfRegistration">;
export type AccountState = ApiSchema<"AccountState">;
export type MessageLocale = ApiSchema<"MessageLocale">;

export interface NewRegistration {
  readonly email: string;
  readonly displayName: string;
  readonly password: string;
  readonly locale: MessageLocale;
}

const SELF_REGISTRATION_MODES = enumMembers<SelfRegistration>({ off: true, with_approval: true, open: true });
export const ACCOUNT_STATES = enumMembers<AccountState>({
  pending_verification: true,
  pending_approval: true,
  active: true,
  disabled: true,
  rejected: true,
});
const MESSAGE_LOCALES = enumMembers<MessageLocale>({ en: true, de: true });

/** The mail language for a UI language such as `de-DE`; English otherwise. */
export function messageLocaleOf(language: string): MessageLocale {
  const primary = language.split("-")[0] ?? "";
  return MESSAGE_LOCALES.find((locale) => locale === primary) ?? "en";
}

export function readRegistrationStatus(signal?: AbortSignal): Promise<RegistrationStatus> {
  return requestJson({
    method: "GET",
    path: "/accounts/registration",
    signal,
    decodeResponse: (body) => {
      const status = readObject(body, "registration status");
      return {
        selfRegistration: readOneOf(status, "self_registration", SELF_REGISTRATION_MODES),
        emailDelivery: readBoolean(status, "email_delivery"),
      };
    },
  });
}

/** Register. The answer is the same whether or not the email has an account. */
export function register(registration: NewRegistration): Promise<void> {
  const body = {
    email: registration.email,
    display_name: registration.displayName,
    password: registration.password,
    locale: registration.locale,
  } satisfies ApiSchema<"RegistrationBody">;
  return requestAccepted("/accounts/registrations", body);
}

/** Ask for a new verification link; answered alike for every email. */
export function requestEmailVerification(email: string): Promise<void> {
  return requestAccepted("/accounts/email-verifications", { email } satisfies ApiSchema<"EmailRequestBody">);
}

/** Use the link of a verification mail once; the account's state afterwards. */
export function confirmEmailVerification(token: string): Promise<AccountState> {
  const body = { token } satisfies ApiSchema<"EmailLinkConfirmationBody">;
  return requestJson({
    method: "POST",
    path: "/accounts/email-verifications/confirmation",
    body: jsonBody(body),
    decodeResponse: (response) => readOneOf(readObject(response, "email verification"), "account_state", ACCOUNT_STATES),
  });
}

/** Ask for a password reset link; answered alike for every email. */
export function requestPasswordReset(email: string): Promise<void> {
  return requestAccepted("/accounts/password-resets", { email } satisfies ApiSchema<"EmailRequestBody">);
}

/** Set a new password with the link of a reset mail, once. Every session of the account ends. */
export function confirmPasswordReset(token: string, newPassword: string): Promise<void> {
  const body = { token, new_password: newPassword } satisfies ApiSchema<"PasswordResetConfirmationBody">;
  return requestWithoutContent({ method: "POST", path: "/accounts/password-resets/confirmation", body: jsonBody(body) });
}

function requestAccepted(path: `/${string}`, body: object): Promise<void> {
  return requestJson({
    method: "POST",
    path,
    body: jsonBody(body),
    decodeResponse: (response) => {
      readLiteral(readObject(response, "accepted request"), "status", "accepted");
    },
  });
}

function decodeUser(user: JsonObject): SignedInAccount {
  return {
    userId: readString(user, "id"),
    email: readString(user, "email"),
    displayName: readString(user, "display_name"),
    accountRole: readOneOf(user, "account_role", ACCOUNT_ROLES),
  };
}

function decodeBrowserSession(session: JsonObject): BrowserSession {
  return {
    account: decodeUser(readObject(session["user"], "session user")),
    // `null` only for bearer sessions; a cookie session without it breaks the contract.
    csrfToken: readString(session, "csrf_token"),
    expiresAt: readString(session, "expires_at"),
    decisions: readAccessDecisions(session["decisions"], null),
  };
}
